import React from "react";
import { motion } from "framer-motion";
import {
  Network,
  Clock4,
  Zap,
  Maximize2,
  Layers,
  ShieldCheck,
  Recycle,
  UserCheck,
} from "lucide-react";
import { BENEFITS } from "@/constants/testIds";

const shipper = [
  {
    id: "capacity",
    icon: Network,
    title: "Access to capacity",
    desc: "Access to a greater network through a single point of contact.",
  },
  {
    id: "ease",
    icon: Clock4,
    title: "Ease of doing business",
    desc: "Time-efficient load distribution anywhere in Southern Africa, with proficiency and ease.",
  },
  {
    id: "efficiency",
    icon: Zap,
    title: "Efficiency",
    desc: "Shared economy with supply chain collaborations and industry expertise, driving transport efficiencies.",
  },
  {
    id: "scalability",
    icon: Maximize2,
    title: "Scalability",
    desc: "We scale according to your capacity requirements as your business moves through its demand cycles.",
  },
  {
    id: "single",
    icon: Layers,
    title: "Single Supplier",
    desc: "No need to manage multiple service providers — reducing complexity and saving you time.",
  },
  {
    id: "compliance",
    icon: ShieldCheck,
    title: "Carrier Compliance",
    desc: "Every carrier on our books is compliant with the NRTA, OHS Act, industry-specific insurance and SARS.",
  },
];

const carrier = [
  {
    id: "shared",
    icon: Recycle,
    title: "Shared Economy",
    desc: "Direct access to volumes through a larger network — fewer empty trips and quicker turnaround times.",
  },
  {
    id: "single-customer",
    icon: UserCheck,
    title: "Single Customer",
    desc: "No need to manage multiple customers. One point of contact, one payment, one relationship.",
  },
];

function Card({ b, i }) {
  const Icon = b.icon;
  return (
    <motion.div
      data-testid={BENEFITS.card(b.id)}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
      className="group bg-white rounded-2xl border border-[#E2E8F0] p-7 md:p-8 hover:border-[#0070C0]/40 hover:-translate-y-1 hover:shadow-[0_20px_50px_-25px_rgba(0,32,80,0.3)] transition-all duration-300 h-full"
    >
      <div className="h-12 w-12 rounded-xl bg-[#0070C0]/10 group-hover:bg-[#0070C0] flex items-center justify-center transition-colors">
        <Icon
          className="h-6 w-6 text-[#0070C0] group-hover:text-white transition-colors"
          strokeWidth={1.75}
        />
      </div>
      <h3 className="font-display mt-6 text-xl md:text-2xl font-bold text-[#002050] tracking-tight">
        {b.title}
      </h3>
      <p className="mt-3 text-[15px] text-[#1F2937]/75 leading-relaxed">
        {b.desc}
      </p>
    </motion.div>
  );
}

export default function Benefits() {
  return (
    <section
      data-testid={BENEFITS.section}
      className="relative py-24 md:py-32 bg-[#F8FAFC] border-y border-[#E2E8F0]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Shippers */}
        <div id="shipper-benefits" className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              Shipper benefits
            </div>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              Built around what shippers actually need.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-[#1F2937]/75 leading-relaxed">
              Six promises we make to every shipper that books a load with
              FreightXchange — backed by relationships built over more than a
              decade.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {shipper.map((b, i) => (
            <Card key={b.id} b={b} i={i} />
          ))}
        </div>

        {/* Carriers */}
        <div id="carrier-benefits" className="mt-24 md:mt-28 grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              Carrier benefits
            </div>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              For carriers, fewer empty kilometres. More good trips.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-[#1F2937]/75 leading-relaxed">
              Owner-drivers, small fleets, large fleets — join our network and
              get a steady flow of vetted loads from one trusted partner.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {carrier.map((b, i) => (
            <Card key={b.id} b={b} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
